import { z } from "zod";
import { LogicGraph, Shape } from "@loumy/contracts";

// D75 — das Level-`documentSchema` (TDoc) des 2D-Platformer-Editors. Tier 3: NUR hier leben die
// platformer-spezifischen Annahmen (Grid, x/y) — NIE im Framework-Vertrag (Neutralitäts-Prinzip §1.7).
// Ein Level ist deklarative Daten (D51): Grid + platzierte Instanzen (Asset-Prototyp + Overrides +
// optionale instanz-bezogene Logik) + optionale level-globale Logik.

/** Platformer-Rollen (D78): bestimmen, welches Asset der Level-Designer je Schritt-Richtung platziert.
 *  Nur Boden (waagrecht) & Wand (senkrecht) — KEINE Rampe: eine schräge Strecke wird zur Treppe zerlegt
 *  (Modus „stairs") oder als gedrehte Diagonale aus EINEM Asset gerendert (Modus „slope"). */
export const PLATFORMER_ROLES = ["floor", "wall"] as const;
export const PlatformerRoleSchema = z.enum(PLATFORMER_ROLES);
export type PlatformerRole = (typeof PLATFORMER_ROLES)[number];

/** Level-Designer-Modus (D78): „stairs" = schräge Strecke → Boden/Wand-Treppe (aufrecht); „slope" = gedrehte
 *  Diagonale (Rampe aus EINEM Asset). Oben per Switch wählbar. */
export const PLATFORMER_DESIGN_MODES = ["stairs", "slope"] as const;
export type PlatformerDesignMode = (typeof PLATFORMER_DESIGN_MODES)[number];

/** Eine platzierte Instanz: Verweis auf einen Asset-Prototyp + Position + Override-Werte + Logik. */
export const PlatformerInstance = z.object({
  /** Instanz-id (eindeutig im Level, Ziel von entity-ref / LogicGraph). */
  iid: z.string().min(1),
  /** Verweis auf den Asset-Katalog-Prototyp (PlaceableDescriptor.id, D70). */
  assetId: z.string().min(1),
  x: z.number(),
  y: z.number(),
  /** Rotation in Grad (D78); fehlt = 0 (aufrecht). Vom Level-Designer aus der Extrude-Richtung gesetzt (Wand/Rampe). */
  rotation: z.number().optional(),
  /**
   * Gezeichnete Fläche (D85), anker-zentriert um `x/y`. Vom Area-Werkzeug gesetzt (Region-Instanzen:
   * Rechteck/Kreis/Polygon). Wenn gesetzt, ersticht sie die prototyp-globale `view.size` fürs Rendern/Hit-Test/Runtime.
   */
  shape: Shape.optional(),
  /** Abweichungen vom Prototyp, key = "behaviorId.propName" (D70 Prototyp/Instanz-Merge). */
  overrides: z.record(z.string(), z.unknown()).default({}),
  /** Instanz-bezogene Logik (D71); "self" bindet an diese Instanz. */
  logic: LogicGraph.optional(),
});
export type PlatformerInstance = z.infer<typeof PlatformerInstance>;

export const PlatformerLevel = z.object({
  grid: z.object({ cell: z.number().int().positive().default(16) }).default({ cell: 16 }),
  instances: z.array(PlatformerInstance).default([]),
  /** Level-globale Logik (D71). */
  logic: LogicGraph.optional(),
});
export type PlatformerLevel = z.infer<typeof PlatformerLevel>;

// D78 (korrigiert) — ASSET-GLOBALE Konfiguration: Rolle + Skalierung sind Eigenschaften DES ASSETS, NICHT
// des Levels. Sie gelten projektweit (über alle Level) und werden in einer Projekt-Sidecar-Datei
// (`loumy.platformer-placeables.json`, über die ProjectSource, D69) persistiert — nie im Level-`TDoc`.
export const PlatformerPlaceableEntry = z.object({
  /** Rolle für den Level-Designer-Auto-Pick (Boden/Wand). */
  role: PlatformerRoleSchema.optional(),
  /** Skalierungsfaktor (>0, finite); fehlt = 1. Größe = EIN Grid-Quadrat × scale. */
  scale: z.number().finite().positive().optional(),
});
export type PlatformerPlaceableEntry = z.infer<typeof PlatformerPlaceableEntry>;

export const PlatformerPlaceableConfig = z.object({
  /** key = PlaceableDescriptor.id (D70/D82) → placeable-globale Eigenschaften. */
  placeables: z.record(z.string(), PlatformerPlaceableEntry).default({}),
});
export type PlatformerPlaceableConfig = z.infer<typeof PlatformerPlaceableConfig>;

/** Pfad der asset-globalen Config-Sidecar im Projekt-Root (ProjectSource-relativ, D69). */
export const PLATFORMER_PLACEABLE_CONFIG_FILE = "loumy.platformer-placeables.json";

/** Ephemerer (NICHT persistierter) UI-State — koppelt Tools ↔ Grid-Viewport (D66 TUi). */
// D78 — Werkzeuge (über Zifferntasten 1–9 wählbar): „select" = Auswählen/Bewegen/Pfad-Handles ziehen;
// „level-designer" = Extrude füllt Grid-Zellen mit dem Brush-Asset; „moveable-platform" = Extrude erzeugt
// eine bewegliche Plattform mit `list<vec2>`-Pfad. Pan = MMB-Drag, Zoom = Wheel — immer aktiv (kein pan-Tool).
export const PLATFORMER_TOOLS = ["select", "level-designer", "moveable-platform", "area"] as const;
export type PlatformerTool = (typeof PLATFORMER_TOOLS)[number];

/** Form-Modus des Area-Werkzeugs (D85): per Rechtsklick im Leerlauf zyklisch umgeschaltet. */
export const PLATFORMER_AREA_SHAPES = ["rect", "circle", "polygon"] as const;
export type PlatformerAreaShape = (typeof PLATFORMER_AREA_SHAPES)[number];
export interface PlatformerUi {
  activeTool: PlatformerTool;
  /** Zuletzt in der Asset-Palette gewähltes Asset (PlaceableDescriptor.id) = Details-Ziel + moveable-platform-Pinsel + Cursor. */
  brushAssetId?: string;
  /** AUTO-abgeleiteter Rollen-Cache (D78): je Rolle das aktive Brush-Asset — das zuletzt für die Rolle gewählte
   *  Asset bzw. (genau ein Asset mit der Rolle) automatisch dieses (Auto-Sekundär). NICHT manuell zugewiesen; aus
   *  der asset-globalen Config (`placeableRoles`) selbst-geheilt. Der Extrude pickt daraus das Asset je Schritt-Richtung. */
  roleBrushes: Partial<Record<PlatformerRole, string>>;
  zoom: number;
  pan: { x: number; y: number };
  /** Snap-to-Grid aktiv. */
  snap: boolean;
  /** Level-Designer-Modus (D78): „stairs" = Boden/Wand-Treppe (aufrecht), „slope" = gedrehte Diagonale (Rampe). */
  designMode: PlatformerDesignMode;
  /** Aktive Form des Area-Werkzeugs (D85): Rechteck/Kreis/Polygon; RMB im Leerlauf zykelt sie. */
  areaShape: PlatformerAreaShape;
  /** D102 (amendiert) — Block-Objekt-Pick des Logic-Editors aktiv („Im Level anklicken …" im
   *  TARGET-Dropdown eines Blocks): der NÄCHSTE Grid-Klick setzt (unabhängig vom aktiven Werkzeug)
   *  die Objekt-Referenz des anfragenden Block-Feldes; Leerklick/Esc = Abbruch. */
  logicPick?: boolean;
}
